import { getCriticalityLabel } from "@features/ax/utils";
import { logEvent } from "@shared/analytics/analytics";
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { GenericTable } from "@shared/components/GenericTable";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { toast } from "@shared/hooks/use-toast";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Plus } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDeleteSystem, useGetAllSystems } from "../../hooks";
import type { WaterSystem } from "../../types";
import { SystemsFilter } from "../filter/filter";

const getVariant = (value: string): "destructive" | "secondary" | "outline" => {
  switch (value) {
    case "Very High Priority":
    case "Critical":
      return "destructive";
    case "Medium Priority":
      return "secondary";
    case "Low Priority":
      return "outline";
    default:
      return "outline";
  }
};

export const SystemsTable: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [systemToDelete, setSystemToDelete] = useState<WaterSystem | null>(
    null
  );

  // Extract current params from URL
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentSystemType = searchParams.get("systemType") || "";
  const currentStatus = searchParams.get("status") || "";
  const currentCriticality = searchParams.get("criticality") || "";
  const currentStartDate = searchParams.get("startDate") || "";
  const currentEndDate = searchParams.get("endDate") || "";

  // Update current page from URL
  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get("page") || "1");
    setCurrentPage(pageFromUrl);
  }, [searchParams]);

  // Format filters for the hook - similar to your BulkBillTable pattern
  const formatFiltersForHook = () => {
    const hookFilters: Record<string, any> = {
      page: currentPage,
      limit: PAGE_SIZE,
    };

    // Add search term
    if (currentSearch.trim()) {
      hookFilters.search = currentSearch.trim();
    }

    // Add filters - only include non-empty values
    if (currentSystemType && currentSystemType !== "all") {
      hookFilters.systemType = currentSystemType;
    }

    if (currentStatus && currentStatus !== "all") {
      hookFilters.status = Number(currentStatus);
    }

    if (currentCriticality && currentCriticality !== "all") {
      hookFilters.criticality = currentCriticality;
    }

    // Date range filters
    if (currentStartDate) {
      hookFilters.startDate = currentStartDate;
    }

    if (currentEndDate) {
      hookFilters.endDate = currentEndDate;
    }

    return hookFilters;
  };

  // Call API with formatted filters - let the hook handle filtering
  const {
    data: systemsResponse,
    isLoading,
    refetch: refetchSystems,
  } = useGetAllSystems(formatFiltersForHook());

  // Current filters for SystemsFilter component
  const currentFilters = useMemo(
    () => ({
      search: currentSearch,
      systemType: currentSystemType,
      status: currentStatus ? Number(currentStatus) : null,
      dateRange: {
        from: currentStartDate,
        to: currentEndDate,
      },
    }),
    [
      currentSearch,
      currentSystemType,
      currentStatus,
      currentCriticality,
      currentStartDate,
      currentEndDate,
    ]
  );

  // Calculate pagination info - now from hook response
  const totalItems = systemsResponse?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    }),
    [currentPage, totalPages, totalItems, startItem, endItem]
  );

  // Handle apply filters - similar to BulkBillTable pattern
  const handleApplyFilters = (newFilters: any) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    // Handle systemType filter
    if (newFilters.systemType && newFilters.systemType.length > 0) {
      newParams.systemType = Array.isArray(newFilters.systemType)
        ? newFilters.systemType[0]
        : newFilters.systemType;
    } else {
      delete newParams.systemType;
    }

    // Handle status filter
    if (newFilters.status && newFilters.status.length > 0) {
      const statusValue = Array.isArray(newFilters.status)
        ? newFilters.status[0]
        : newFilters.status;
      newParams.status = String(statusValue);
    } else {
      delete newParams.status;
    }

    // Handle criticality filter
    if (newFilters.criticality && newFilters.criticality.length > 0) {
      newParams.criticality = Array.isArray(newFilters.criticality)
        ? newFilters.criticality[0]
        : newFilters.criticality;
    } else {
      delete newParams.criticality;
    }

    // Handle date range filters
    if (newFilters.dateRange?.from) {
      newParams.startDate = newFilters.dateRange.from;
    } else {
      delete newParams.startDate;
    }

    if (newFilters.dateRange?.to) {
      newParams.endDate = newFilters.dateRange.to;
    } else {
      delete newParams.endDate;
    }

    // Reset to page 1 when filters change
    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    // Clear all filter parameters
    delete newParams.systemType;
    delete newParams.status;
    delete newParams.criticality;
    delete newParams.startDate;
    delete newParams.endDate;
    delete newParams.search;

    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  // Handle search - similar to BulkBillTable pattern
  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.search = encodeURIComponent(searchTerm.trim());
    } else {
      delete newParams.search;
    }

    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
    setCurrentPage(page);
  };

  const handleDeleteClick = (system: WaterSystem) => {
    setSystemToDelete(system);
    setShowDeleteDialog(true);
  };

  const { mutate: deleteSystemMutation, isPending: isDeletingSystem } =
    useDeleteSystem();

  const handleConfirmDelete = () => {
    if (systemToDelete) {
      logEvent("System Delete Confirmed", {
        systemId: systemToDelete.id,
      });
      deleteSystemMutation(
        { systemId: systemToDelete.id },
        {
          onSuccess: async () => {
            toast({
              title: "Success",
              description: `"${systemToDelete.systemName}" has been deleted successfully.`,
            });
            setShowDeleteDialog(false);
            setSystemToDelete(null);
          },
          onError: (error) => {
            toast({
              title: "Delete Failed",
              description: error.message,
              variant: "destructive",
            });
          },
        }
      );
    }
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setSystemToDelete(null);
  };

  const columns: TableColumn<WaterSystem>[] = useMemo(
    () => [
      {
        key: "systemNo",
        header: "System ID",
        type: "text",
        sortable: true,
        headerTooltip: "The unique identifier for this system.",
      },
      {
        key: "systemName",
        header: "System Name",
        type: "text",
        sortable: true,
        headerTooltip: "The common name used to identify this system.",
      },
      {
        key: "systemTypeDisplay",
        header: "System Type",
        type: "text",
        sortable: true,
        headerTooltip: "The classification or category of this system.",
      },
      {
        key: "location",
        header: "Location",
        type: "custom",
        headerTooltip: "The specific physical location or zone of the system.",
        render: (value) => <HoverRevealText text={value} />,
      },
      {
        key: "facilityName",
        header: "Facility",
        type: "text",
        headerTooltip:
          "The main facility where this system is located or managed from.",
        render: (value) => value || "N/A",
      },
      {
        key: "commissionedDate",
        header: "Commission Date",
        type: "text",
        headerTooltip:
          "The date this system was officially commissioned or entered service.",
      },
      {
        key: "criticalityLevelDisplay",
        header: "Criticality",
        headerTooltip:
          "An assessment of the system's importance. Higher criticality means its failure has a greater impact.",
        type: "custom",
        render: (value: any, row: any) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{row.criticalityScore}</span>
            <Badge
              variant={getVariant(
                getCriticalityLabel(row.criticalityScore).label
              )}
              className={getCriticalityLabel(row.criticalityScore).color}
            >
              {getCriticalityLabel(row.criticalityScore).label}
            </Badge>
          </div>
        ),
      },
      {
        key: "designCapacity",
        header: "Design Capacity",
        headerTooltip:
          "The maximum output, flow, or volume the system was designed to handle.",
        type: "text",
      },
      {
        key: "currentThroughput",
        header: "Current Capacity",
        headerTooltip:
          "The current or most recent operational output of the system. This data is updated in near real-time.",
        type: "text",
      },
      {
        key: "statusDisplay",
        header: "Operational Status",
        type: "badge",
        sortable: true,
        headerTooltip:
          "The current real-time operational status of the system.",
        badgeConfig: {
          getVariant: (value: string) => {
            switch (value) {
              case "Operational":
                return "default";
              case "Under Maintenance":
                return "secondary";
              case "Decommissioned":
                return "destructive";
              default:
                return "outline";
            }
          },
        },
      },
    ],
    []
  );

  const actions: TableAction<WaterSystem>[] = useMemo(
    () => [
      {
        label: "View Details",
        onClick: (item) =>
          navigate(
            `/ax/asset-registry/systems/view/${item.id}/${item.systemNo}`
          ),
      },
      {
        label: "Edit",
        onClick: (item) =>
          navigate(`/ax/asset-registry/systems/edit/${item.id}`),
      },
      {
        label: "Delete",
        onClick: (item) => handleDeleteClick(item),
      },
    ],
    [navigate]
  );

  const filterPanel = (
    <div className="space-y-4">
      <SystemsFilter
        currentFilters={currentFilters}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
      />
    </div>
  );

  const hasActiveFilters =
    currentSystemType ||
    currentStatus ||
    currentCriticality ||
    currentStartDate ||
    currentEndDate ||
    currentSearch;

  return (
    <div className="space-y-6">
      <GenericTable
        data={systemsResponse?.results ?? []}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search by System ID, Name..."
        searchParamName="search"
        searchDebounceMs={300}
        onSearch={handleSearch}
        pagination={pagination}
        onPageChange={handlePageChange}
        actions={actions}
        onExport={() => {}}
        actionsType="dropdown"
        filterPanel={filterPanel}
        emptyMessage="No systems found matching your criteria"
        emptyIcon={<Plus className="h-8 w-8 text-muted-foreground mb-2" />}
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Filters active
              </span>
            </div>
          ) : null
        }
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        title="Delete System"
        itemName={systemToDelete?.systemName}
        description={`Are you sure you want to delete the system "${systemToDelete?.systemName}" (ID: ${systemToDelete?.systemNo})? This will permanently remove the system and all its associated data. This action cannot be undone.`}
        isLoading={isDeletingSystem}
      />
    </div>
  );
};
